import { useEffect, useRef, RefObject } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

type AnimationType = 'fadeUp' | 'fadeIn' | 'slideLeft' | 'slideRight' | 'scaleUp';

interface ScrollAnimationOptions {
  type?: AnimationType;
  delay?: number;
  duration?: number;
  distance?: number;
  start?: string;
  stagger?: boolean;
  staggerAmount?: number;
  staggerSelector?: string;
  scrub?: boolean;
  once?: boolean;
}

const getFromVars = (type: AnimationType, distance: number): gsap.TweenVars => {
  switch (type) {
    case 'fadeUp':
      return { opacity: 0, y: distance };
    case 'fadeIn':
      return { opacity: 0 };
    case 'slideLeft':
      return { opacity: 0, x: distance };
    case 'slideRight':
      return { opacity: 0, x: -distance };
    case 'scaleUp':
      return { opacity: 0, scale: 0.92 };
  }
};

const getToVars = (): gsap.TweenVars => ({
  opacity: 1,
  y: 0,
  x: 0,
  scale: 1,
});

/**
 * Hook for scroll-triggered animations.
 */
export function useScrollAnimation<T extends HTMLElement = HTMLDivElement>(
  options: ScrollAnimationOptions = {},
): RefObject<T> {
  const ref = useRef<T>(null);

  const {
    type = 'fadeUp',
    delay = 0,
    duration = 0.8,
    distance = 40,
    start = 'top 85%',
    stagger = false,
    staggerAmount = 0.12,
    staggerSelector = ':scope > *',
    once = true,
  } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ease = type === 'fadeUp' || type === 'slideLeft' || type === 'slideRight' ? 'power3.out' : 'power2.out';
    const fromVars = getFromVars(type, distance);
    const toVars = { ...getToVars(), duration, delay, ease };

    if (stagger) {
      const children = el.querySelectorAll(staggerSelector);
      if (children.length === 0) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start,
          toggleActions: once ? 'play none none none' : 'play none none reverse',
        },
      });

      tl.fromTo(children, fromVars, { ...toVars, stagger: staggerAmount });

      return () => {
        tl.kill();
        // Reset to visible state on cleanup (handles React Strict Mode)
        gsap.set(children, getToVars());
      };
    } else {
      const tween = gsap.fromTo(el, fromVars, {
        ...toVars,
        scrollTrigger: {
          trigger: el,
          start,
          toggleActions: once ? 'play none none none' : 'play none none reverse',
        },
      });

      return () => {
        tween.kill();
        gsap.set(el, getToVars());
      };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return ref;
}

/**
 * Hook for parallax scrolling effect.
 */
export function useParallax<T extends HTMLElement = HTMLDivElement>(speed: number = 0.3): RefObject<T> {
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const tween = gsap.to(el, {
      yPercent: speed * 100,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
    });

    return () => {
      tween.kill();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return ref;
}

/**
 * Hook for entrance animation (plays immediately, no scroll trigger).
 * Uses fromTo for robustness with React Strict Mode.
 */
export function useEntranceAnimation<T extends HTMLElement = HTMLDivElement>(options: {
  stagger?: boolean;
  staggerAmount?: number;
  staggerSelector?: string;
  duration?: number;
  delay?: number;
  distance?: number;
}): RefObject<T> {
  const ref = useRef<T>(null);

  const {
    stagger = true,
    staggerAmount = 0.15,
    staggerSelector = ':scope > *',
    duration = 0.7,
    delay = 0.1,
    distance = 30,
  } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fromVars = { opacity: 0, y: distance };
    const toVars = { opacity: 1, y: 0, duration, ease: 'power3.out' };

    if (stagger) {
      const children = el.querySelectorAll(staggerSelector);
      if (children.length === 0) return;

      const tl = gsap.timeline({ delay });
      tl.fromTo(children, fromVars, { ...toVars, stagger: staggerAmount });

      return () => {
        tl.kill();
        // Ensure elements are visible after cleanup (React Strict Mode)
        gsap.set(children, { opacity: 1, y: 0 });
      };
    } else {
      const tl = gsap.timeline({ delay });
      tl.fromTo(el, fromVars, toVars);

      return () => {
        tl.kill();
        gsap.set(el, { opacity: 1, y: 0 });
      };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return ref;
}
